import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, AlertCircle } from 'lucide-react';

const Planning = ({ user }) => {
  const [calendarCode, setCalendarCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('atlantique');
  const [sites, setSites] = useState([]);

  // Les stagiaires ne peuvent pas changer de centre
  const isStagiaire = user?.role === 'stagiaire' || user?.role === 'moniteur_vnm';

  useEffect(() => {
    // Pour les stagiaires, utiliser leur centre s'il est défini
    if (isStagiaire && user?.centre) {
      setSelectedSite(user.centre);
    }
  }, [user, isStagiaire]);

  useEffect(() => {
    if (selectedSite) {
      loadCalendarCode();
    }
  }, [selectedSite]);

  const loadCalendarCode = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/centre/profiles');
      let siteProfile = null;
      
      if (response.data && response.data.sites) {
        setSites(response.data.sites);
        siteProfile = response.data.sites.find(s => 
          s.site_id?.toLowerCase() === selectedSite.toLowerCase() || 
          s.site_name?.toLowerCase() === selectedSite.toLowerCase()
        );
      } else if (response.data) {
        // Ancien format
        const siteKey = selectedSite.charAt(0).toUpperCase() + selectedSite.slice(1);
        siteProfile = response.data[siteKey];
        setSites([
          { site_id: 'atlantique', site_name: 'Atlantique', ...response.data.Atlantique },
          { site_id: 'occitanie', site_name: 'Occitanie', ...response.data.Occitanie }
        ]);
      }
      
      if (siteProfile && siteProfile.google_calendar_code) {
        setCalendarCode(siteProfile.google_calendar_code);
      } else {
        setCalendarCode('');
      }
    } catch (error) {
      setCalendarCode('');
    } finally {
      setLoading(false);
    }
  };

  // Construire l'URL de l'iframe à partir du code
  const getCalendarUrl = () => {
    if (!calendarCode) return '';
    // Si le code contient déjà une URL complète ou un iframe
    if (calendarCode.includes('calendar.google.com/calendar/embed')) {
      // Extraire l'URL src si c'est un iframe
      const srcMatch = calendarCode.match(/src="([^"]+)"/);
      if (srcMatch) return srcMatch[1];
      return calendarCode;
    }
    // Sinon, construire l'URL à partir du code
    const encodedCode = encodeURIComponent(calendarCode);
    return `https://calendar.google.com/calendar/embed?src=${encodedCode}&ctz=Europe%2FParis`;
  };

  const calendarUrl = getCalendarUrl();

  return (
    <div className="space-y-6" data-testid="planning-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Planning de Formation</h1>
          <p className="text-slate-600">Consultez le calendrier des formations et événements</p>
        </div>
        
        {/* Sélecteur de site - masqué pour les stagiaires */}
        {!isStagiaire && sites.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Centre :</span>
            <div className="flex gap-2">
              {sites.map(site => (
                <button
                  key={site.site_id}
                  onClick={() => setSelectedSite(site.site_id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    selectedSite === site.site_id || selectedSite.toLowerCase() === site.site_name?.toLowerCase()
                      ? 'bg-[#8DC63F] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`site-selector-${site.site_id}`}
                >
                  {site.site_name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200">
          <p className="text-sm text-slate-600">
            Les modifications de planning génèrent automatiquement des notifications
          </p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
          </div>
        ) : calendarUrl ? (
          <div className="aspect-video w-full" data-testid="calendar-container">
            <iframe
              src={calendarUrl}
              style={{ border: 0 }}
              width="100%"
              height="600"
              frameBorder="0"
              scrolling="no"
              title="Planning Google Calendar"
              className="w-full h-full"
            ></iframe>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-96 text-center p-6">
            <AlertCircle className="h-12 w-12 text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Aucun agenda configuré</h3>
            <p className="text-slate-600 max-w-md">
              Le code d'intégration Google Calendar n'est pas configuré pour ce centre.
              <br />
              Rendez-vous dans <strong>Profil Centre</strong> pour ajouter le code d'intégration.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Planning;